import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formasPagoAPI, cajasAPI, cuentasBancariasAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../Clientes.css';

const FormaPagoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cajas, setCajas] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    activo: true,
    tipo_movimiento: 'ambos',
    destino_tipo: 'caja',
    caja_id: '',
    cuenta_bancaria_id: '',
    mostrar_saldo_dashboard: false
  });

  const loadCajas = useCallback(async () => {
    try {
      const res = await cajasAPI.getAll({});
      if (res.success) setCajas(res.data || []);
    } catch (e) {
      console.error('Error al cargar cajas', e);
    }
  }, []);

  const loadCuentasBancarias = useCallback(async () => {
    try {
      const res = await cuentasBancariasAPI.getAll({ activo: true });
      if (res.success) setCuentasBancarias(res.data || []);
    } catch (e) {
      console.error('Error al cargar cuentas bancarias', e);
    }
  }, []);

  const loadFormaPago = useCallback(async () => {
    try {
      const response = await formasPagoAPI.getById(id);
      if (response.success) {
        const d = response.data;
        let destino_tipo = 'caja';
        if (d.cuenta_seleccion_al_usar) destino_tipo = 'cuenta_al_usar';
        else if (d.caja_id) destino_tipo = 'caja';
        else if (d.cuenta_bancaria_id) destino_tipo = 'cuenta';
        setFormData({
          nombre: d.nombre || '',
          descripcion: d.descripcion || '',
          activo: Boolean(d.activo),
          tipo_movimiento: d.tipo_movimiento && ['ingreso', 'egreso', 'ambos'].includes(d.tipo_movimiento) ? d.tipo_movimiento : 'ambos',
          destino_tipo,
          caja_id: d.caja_id || '',
          cuenta_bancaria_id: d.cuenta_bancaria_id || '',
          mostrar_saldo_dashboard: Boolean(d.mostrar_saldo_dashboard)
        });
      }
    } catch (error) {
      toast.error('Error al cargar forma de pago');
      navigate('/formas-pago');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadCajas();
    loadCuentasBancarias();
  }, [loadCajas, loadCuentasBancarias]);

  useEffect(() => {
    if (id) {
      loadFormaPago();
    }
  }, [id, loadFormaPago]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'destino_tipo') {
        next.caja_id = '';
        next.cuenta_bancaria_id = '';
      }
      return next;
    });
  };

  const esChequePropio = String(formData.nombre || '').trim().toLowerCase() === 'cheque propio';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const cuentaAlUsar = formData.destino_tipo === 'cuenta_al_usar';
    if (!esChequePropio && !cuentaAlUsar) {
      if (formData.destino_tipo === 'caja' && !formData.caja_id) {
        toast.error('Seleccione una caja');
        setLoading(false);
        return;
      }
      if (formData.destino_tipo === 'cuenta' && !formData.cuenta_bancaria_id) {
        toast.error('Seleccione una cuenta bancaria');
        setLoading(false);
        return;
      }
    }

    const payload = {
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      activo: formData.activo,
      tipo_movimiento: esChequePropio ? 'egreso' : formData.tipo_movimiento,
      caja_id: esChequePropio || cuentaAlUsar ? null : (formData.destino_tipo === 'caja' ? formData.caja_id : null),
      cuenta_bancaria_id: esChequePropio || cuentaAlUsar ? null : (formData.destino_tipo === 'cuenta' ? formData.cuenta_bancaria_id : null),
      cuenta_seleccion_al_usar: cuentaAlUsar,
      mostrar_saldo_dashboard: formData.mostrar_saldo_dashboard
    };

    try {
      let response;
      if (id) {
        response = await formasPagoAPI.update(id, payload);
      } else {
        response = await formasPagoAPI.create(payload);
      }

      if (response.success) {
        toast.success(id ? 'Forma de pago actualizada exitosamente' : 'Forma de pago creada exitosamente');
        navigate('/formas-pago');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar forma de pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cliente-form">
      <h1>{id ? 'Editar Forma de Pago' : 'Nueva Forma de Pago'}</h1>
      
      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-group">
          <label>Nombre *</label>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
            placeholder="Ej: Efectivo, Transferencia, Cheque, etc."
          />
        </div>

        <div className="form-group">
          <label>Descripción</label>
          <input
            type="text"
            name="descripcion"
            value={formData.descripcion}
            onChange={handleChange}
            placeholder="Descripción opcional"
          />
        </div>

        {!esChequePropio && (
          <>
            <div className="form-group">
              <label style={{ marginBottom: '8px', display: 'block' }}>Uso</label>
              <div className="asignacion-destino" style={{ marginTop: '6px' }}>
                <label className={`asignacion-opcion ${formData.tipo_movimiento === 'ingreso' ? 'asignacion-opcion-activa' : ''}`} style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="tipo_movimiento"
                    value="ingreso"
                    checked={formData.tipo_movimiento === 'ingreso'}
                    onChange={handleChange}
                    className="asignacion-radio"
                  />
                  <span className="asignacion-icono">↓</span>
                  <span className="asignacion-texto">Ingreso</span>
                </label>
                <label className={`asignacion-opcion ${formData.tipo_movimiento === 'egreso' ? 'asignacion-opcion-activa' : ''}`} style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="tipo_movimiento"
                    value="egreso"
                    checked={formData.tipo_movimiento === 'egreso'}
                    onChange={handleChange}
                    className="asignacion-radio"
                  />
                  <span className="asignacion-icono">↑</span>
                  <span className="asignacion-texto">Egreso</span>
                </label>
                <label className={`asignacion-opcion ${formData.tipo_movimiento === 'ambos' ? 'asignacion-opcion-activa' : ''}`} style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="tipo_movimiento"
                    value="ambos"
                    checked={formData.tipo_movimiento === 'ambos'}
                    onChange={handleChange}
                    className="asignacion-radio"
                  />
                  <span className="asignacion-icono">↕</span>
                  <span className="asignacion-texto">Ambos</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label style={{ marginBottom: '10px', display: 'block' }}>Asignar a (caja o cuenta bancaria) *</label>
              <div className="asignacion-destino">
                <div
                  className={`asignacion-opcion ${formData.destino_tipo === 'caja' ? 'asignacion-opcion-activa' : ''}`}
                  onClick={() => handleChange({ target: { name: 'destino_tipo', value: 'caja' } })}
                  onKeyDown={(e) => e.key === 'Enter' && handleChange({ target: { name: 'destino_tipo', value: 'caja' } })}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    type="radio"
                    name="destino_tipo"
                    value="caja"
                    checked={formData.destino_tipo === 'caja'}
                    onChange={handleChange}
                    className="asignacion-radio"
                  />
                  <span className="asignacion-icono">📦</span>
                  <span className="asignacion-texto">Caja</span>
                </div>
                <div
                  className={`asignacion-opcion ${formData.destino_tipo === 'cuenta' ? 'asignacion-opcion-activa' : ''}`}
                  onClick={() => handleChange({ target: { name: 'destino_tipo', value: 'cuenta' } })}
                  onKeyDown={(e) => e.key === 'Enter' && handleChange({ target: { name: 'destino_tipo', value: 'cuenta' } })}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    type="radio"
                    name="destino_tipo"
                    value="cuenta"
                    checked={formData.destino_tipo === 'cuenta'}
                    onChange={handleChange}
                    className="asignacion-radio"
                  />
                  <span className="asignacion-icono">🏦</span>
                  <span className="asignacion-texto">Cuenta bancaria fija</span>
                </div>
                <div
                  className={`asignacion-opcion ${formData.destino_tipo === 'cuenta_al_usar' ? 'asignacion-opcion-activa' : ''}`}
                  onClick={() => handleChange({ target: { name: 'destino_tipo', value: 'cuenta_al_usar' } })}
                  onKeyDown={(e) => e.key === 'Enter' && handleChange({ target: { name: 'destino_tipo', value: 'cuenta_al_usar' } })}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    type="radio"
                    name="destino_tipo"
                    value="cuenta_al_usar"
                    checked={formData.destino_tipo === 'cuenta_al_usar'}
                    onChange={handleChange}
                    className="asignacion-radio"
                  />
                  <span className="asignacion-icono">🏦</span>
                  <span className="asignacion-texto">Cuenta al usar</span>
                </div>
              </div>
              {formData.destino_tipo === 'cuenta_al_usar' && (
                <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: '8px' }}>
                  La cuenta bancaria se elige al registrar cada recibo u orden de pago (recomendado para transferencias).
                </p>
              )}
              {formData.destino_tipo === 'caja' && (
                <div className="asignacion-select-wrap">
                  <label htmlFor="forma-pago-caja-id">Seleccione la caja</label>
                  <select
                    id="forma-pago-caja-id"
                    name="caja_id"
                    value={formData.caja_id}
                    onChange={handleChange}
                    className="asignacion-select"
                  >
                    <option value="">Seleccione una caja</option>
                    {cajas.filter(c => c.activa).map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              )}
              {formData.destino_tipo === 'cuenta' && (
                <div className="asignacion-select-wrap">
                  <label htmlFor="forma-pago-cuenta-id">Seleccione la cuenta</label>
                  <select
                    id="forma-pago-cuenta-id"
                    name="cuenta_bancaria_id"
                    value={formData.cuenta_bancaria_id}
                    onChange={handleChange}
                    className="asignacion-select"
                  >
                    <option value="">Seleccione una cuenta</option>
                    {cuentasBancarias.map(cb => (
                      <option key={cb.id} value={cb.id}>
                        {cb.banco_nombre || 'Banco'} - {cb.numero_cuenta} ({cb.tipo})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </>
        )}
        {esChequePropio && id && (
          <p style={{ color: '#7f8c8d', fontSize: '14px', marginBottom: '12px' }}>
            Cheque Propio es solo para egreso y no se asigna a caja ni cuenta; la cuenta se elige al cargar cada cheque en el recibo.
          </p>
        )}

        <div className="form-group">
          <label className="form-toggle-wrap">
            <span className="toggle-switch-box">
              <input
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
              />
              <span className="form-toggle-switch" />
            </span>
            <span className="form-toggle-label">{formData.activo ? 'Desactivar' : 'Activar'}</span>
          </label>
        </div>

        <div className="form-group">
          <label className="form-toggle-wrap">
            <span className="toggle-switch-box">
              <input
                type="checkbox"
                name="mostrar_saldo_dashboard"
                checked={formData.mostrar_saldo_dashboard}
                onChange={handleChange}
              />
              <span className="form-toggle-switch" />
            </span>
            <span className="form-toggle-label">Mostrar saldo en el Dashboard</span>
          </label>
          <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
            Si está activo, en el Dashboard se verá el saldo a fecha desde, movimientos del período y saldo final.
          </small>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/formas-pago')} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormaPagoForm;

